'use client';

import clsx from 'clsx';
import { useInView } from 'react-intersection-observer';

import styles from './styles.module.css';

import { BranchQRCode } from '@/components/BranchQRCode';
import { GET_APP_LINK } from '@/modules/branch/constants';
import {
  APP_DOWNLOAD_PLACEMENTS,
  type AppDownloadPlacement,
  type AppDownloadPlacementEntry,
} from '@/modules/branch/placements';
import { useAppDownloadDeepLink } from '@/modules/branch/useAppDownloadDeepLink';

type Props = {
  placement: AppDownloadPlacement;
  size?: 'sm' | 'md' | 'lg' | number;
  withDepopLogo?: boolean;
  sourceId?: string;
  ariaLabel?: string;
};

function DeepLinkQRCode({
  placement,
  sourceId,
  showLogo,
  ariaLabel,
}: Pick<Props, 'placement' | 'sourceId' | 'ariaLabel'> & {
  showLogo: boolean;
}) {
  const value = useAppDownloadDeepLink({ placement, sourceId });

  return (
    <BranchQRCode
      value={value}
      showLogo={showLogo}
      ariaLabel={ariaLabel}
      fillParent
    />
  );
}

export function DownloadAppQRCode({
  placement,
  size = 'sm',
  withDepopLogo = false,
  sourceId,
  ariaLabel,
}: Props) {
  const { ref, inView } = useInView({ triggerOnce: true });
  const entry: AppDownloadPlacementEntry = APP_DOWNLOAD_PLACEMENTS[placement];

  return (
    <div className={styles.container}>
      <div
        ref={ref}
        className={clsx(styles.qrCodeDynamic, {
          [styles['qrCodeDynamic--md']]: size === 'md',
          [styles['qrCodeDynamic--lg']]: size === 'lg',
        })}
        style={typeof size === 'number' ? { width: size } : undefined}
      >
        {inView ? (
          <DeepLinkQRCode
            placement={placement}
            sourceId={sourceId}
            showLogo={withDepopLogo}
            ariaLabel={ariaLabel}
          />
        ) : (
          <BranchQRCode
            value={entry.fallback ?? GET_APP_LINK}
            showLogo={withDepopLogo}
            ariaLabel={ariaLabel}
            fillParent
          />
        )}
      </div>
    </div>
  );
}
